import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Entry } from "../models/Entry";
import { upsertEntry } from "../repository/LogbookRepository";

export type EntrySubmissionData = {
	assetId: number | null;
	entryId: number | null;
	shiftId: number;
	isForOtherShift?: boolean;
	title: string;
	content: string;
	author: string;
	createTimestamp: Date;
};

type UseEntrySubmissionOptions = {
	onSuccess?: (entry: Entry | null) => void;
	onError?: (error: Error) => void;
	isNewEntry?: boolean;
	onHide?: (entry?: number | null, shouldStayInEditMode?: boolean) => void;
};

export const useEntrySubmission = ({
	onSuccess,
	onError,
	isNewEntry = false,
	onHide = () => {},
}: UseEntrySubmissionOptions = {}) => {
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
	const [pendingSubmitData, setPendingSubmitData] =
		useState<EntrySubmissionData | null>(null);

	const submit = useMutation(upsertEntry, {
		onSuccess: async (entry: Entry | null) => {
			if (entry && entry.EntryId) {
				onSuccess?.(entry);
			} else {
				console.error("No EntryId received from API response", entry);
				onError?.(new Error("Failed to get entry ID from server response"));
			}
		},
		onError: (error: Error) => {
			onError?.(error);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const submitEntry = async (formData: EntrySubmissionData) => {
		setIsSubmitLoading(true);

		const submissionData = { ...formData };
		if (isNewEntry && !submissionData.createTimestamp) {
			submissionData.createTimestamp = new Date();
		}
		if (!submissionData.shiftId || submissionData.isForOtherShift) {
			setPendingSubmitData(submissionData);
			setShowConfirmationDialog(true);
			setIsSubmitLoading(false);
		} else {
			await submit.mutate({
				assetId: submissionData.assetId,
				shiftId: submissionData.shiftId,
				entryId: submissionData.entryId,
				title: submissionData.title,
				content: submissionData.content,
				author: submissionData.author ?? "",
				createTimestamp: submissionData.createTimestamp!,
			});
		}
	};

	const handleConfirmationClose = (value?: string) => {
		setShowConfirmationDialog(false);
		if (value === "OK" && pendingSubmitData) {
			onHide();
			submit.mutate({
				assetId: pendingSubmitData.assetId,
				shiftId: pendingSubmitData.shiftId,
				entryId: pendingSubmitData.entryId,
				title: pendingSubmitData.title,
				content: pendingSubmitData.content,
				author: pendingSubmitData.author ?? "",
				createTimestamp: pendingSubmitData.createTimestamp!,
			});
		}
		setPendingSubmitData(null);
	};

	return {
		submitEntry,
		isSubmitLoading,
		showConfirmationDialog,
		pendingSubmitData,
		handleConfirmationClose,
		mutation: submit,
	};
};
